import {
  UsersApi,
  Configuration,
  LocationApi,
  AuthorizationApi,
  ConfigurationApi,
  DictionaryApi,
  RoomApi,
  DockApi,
  DeviceStateApi,
  SnapInApi,
  SceneApi,
  DeviceConfigurationApi,
  HomeOwnerAuthenticationApi,
  HomeOwnerApi,
  LocationConfigurationSnapshotApi
} from '../../api';
import { ISelectOption } from '../../components/Select/Select.types';
import { IAccount } from '../../models';
import { getAccount } from '../auth.util';

export {
  LocationStatusEnum,
  UserDto,
  UserNewDto,
  ConnectionConfigurationDto,
  LocationInstallerDto,
  LocationDto,
  LocationNewDto,
  RoomNewDto,
  RoomDto,
  AuthorizationTypeEnum
} from '../../api';

export { RequiredError } from '../../api/base';

export const basePath = process.env.API_URL;

export const getBaseConfiguration = (
  account: IAccount,
  customPath?: string
): Configuration => {
  const configuration = new Configuration();
  configuration.basePath = customPath || basePath;
  configuration.apiKey = account.accessToken;
  return configuration;
};

export const getHomeOwnerAuthenticationApi = (
  token?: string
): HomeOwnerAuthenticationApi => {
  const configuration = new Configuration();
  configuration.basePath = basePath;
  if (token) {
    configuration.apiKey = token;
  }
  return new HomeOwnerAuthenticationApi(configuration);
};

export const getHomeOwnerApi = (account): HomeOwnerApi => {
  return new HomeOwnerApi(getBaseConfiguration(account));
};

export const getAdminAuthorizationApi = (token?: string) => {
  const configuration = new Configuration();
  configuration.basePath = basePath;
  if (token) {
    configuration.apiKey = token;
  }
  return new AuthorizationApi(configuration);
};

export const getUsersApi = (account): UsersApi => {
  return new UsersApi(getBaseConfiguration(account));
};

export const getLocationsSnapshotsApi = (
  account
): LocationConfigurationSnapshotApi => {
  return new LocationConfigurationSnapshotApi(getBaseConfiguration(account));
};

export const getScenesApi = (account): SceneApi => {
  return new SceneApi(getBaseConfiguration(account));
};

export const getLocationsApi = (account): LocationApi => {
  return new LocationApi(getBaseConfiguration(account));
};

export const getDeviceStatesApi = (account): DeviceStateApi => {
  return new DeviceStateApi(getBaseConfiguration(account));
};

export const getRoomsApi = (account): RoomApi => {
  return new RoomApi(getBaseConfiguration(account));
};

export const getDeviceConfigurationApi = (account): DeviceConfigurationApi => {
  return new DeviceConfigurationApi(getBaseConfiguration(account));
};

export const getDocksApi = (account): DockApi => {
  return new DockApi(getBaseConfiguration(account));
};

export const getSnapinApi = (account): SnapInApi => {
  return new SnapInApi(getBaseConfiguration(account));
};

export const getDictionaryApi = (account): DictionaryApi => {
  return new DictionaryApi(getBaseConfiguration(account));
};

export const getConfigurationApi = (account): ConfigurationApi => {
  return new ConfigurationApi(getBaseConfiguration(account));
};

export const getUserEmptyModel = () => {
  return {
    id: null,
    userName: null,
    disabled: false,
    userRoleList: [],
    password: '',
    version: 0
  };
};

export const getLocationEmptyModel = () => {
  return {
    bcbConnectedStatus: '',
    details: {},
    owner: {},
    rooms: [],
    docks: {},
    installers: [],
    connectionConfigurations: []
  };
};

export const getSnapInTypesOptions = (
  group?: string,
  getHomeOwnerAccount?: any
): Promise<ISelectOption[]> => {
  return new Promise((resolve, reject) => {
    getDictionaryApi(getHomeOwnerAccount || getAccount())
      .dictionaryGetSnapInTypes()
      .then(result => {
        if (result) {
          resolve(
            result.data
              .map(snapIn => {
                return snapIn.snapInCodeDockPositionSymbols.map(
                  snapInPositionSymbol => ({
                    value: snapIn.symbol,
                    group: `${snapInPositionSymbol}${snapIn.snapInCodeDockTypeSymbolValue}`,
                    groupLabel: null,
                    groupLabelVisible: null,
                    label: `[${snapInPositionSymbol}] ${snapIn.name}`
                  })
                );
              })
              // @ts-ignore
              .flat()
              .filter(s => s.group === group || !group)
          );
        } else {
          reject();
        }
      })
      .catch(err => {
        reject(err);
      });
  });
};

export const getDictionarySelectOptions = (
  dictName: string
): Promise<ISelectOption[]> => {
  return new Promise<ISelectOption[]>((resolve, reject) => {
    getDictionaryApi(getAccount())
      .dictionaryGetDict(dictName)
      .then(result => {
        if (result && result.data) {
          resolve(
            Object.keys(result.data)
              .map(key => {
                return { value: key, label: result.data[key] };
              })
              .sort((a, b) => {
                const textA = a.label.toUpperCase();
                const textB = b.label.toUpperCase();
                // eslint-disable-next-line no-nested-ternary
                return textA < textB ? -1 : textA > textB ? 1 : 0;
              })
          );
        } else {
          reject();
        }
      });
  });
};

export const getSnapInPositions = (): Promise<ISelectOption[]> => {
  return getDictionarySelectOptions('Position');
};
